import { useEffect, useState, useMemo } from 'react'
import { supabase } from '../../lib/supabase'
import { formatUSD, formatCOP } from '../../constants/pricing'
import {
  TrendingUp, DollarSign, Users, Target, Download, Loader, AlertCircle,
} from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts'

const PHASE_NAMES = [
  'Sin empezar', 'Etapa 0 — Modelo de Negocio', 'Etapa 1 — Diagnóstico',
  'Etapa 2 — Definición', 'Etapa 3 — Mercado', 'Etapa 4 — Estrategia UN',
  'Etapa 5 — Implementación', 'Etapa 6 — Plan Corporativo',
]

/**
 * Reads the database views created in `migration_reminders_and_analytics.sql`:
 *   • caja_metrics         — single-row top-line metrics (MRR, ARR, totals)
 *   • caja_signup_cohorts  — monthly signup → conversion table
 *   • caja_phase_funnel    — counts of projects by furthest reached phase
 *
 * Top-line cards are computed in SQL (not JS) so the numbers match what we'd
 * report for accounting. Funnel/cohorts feed simple Recharts visualizations.
 */
export default function AdminMetricsView() {
  const [metrics, setMetrics]   = useState(null)
  const [cohorts, setCohorts]   = useState([])
  const [funnel, setFunnel]     = useState([])
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState('')
  const [csvBusy, setCsvBusy]   = useState(false)

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    setLoading(true)
    setError('')
    try {
      const [m, c, f] = await Promise.all([
        supabase.from('caja_metrics').select('*').maybeSingle(),
        supabase.from('caja_signup_cohorts').select('*').limit(12),
        supabase.from('caja_phase_funnel').select('*'),
      ])
      if (m.error) throw m.error
      setMetrics(m.data ?? null)
      setCohorts(c.data ?? [])
      setFunnel(f.data ?? [])
    } catch (err) {
      setError(err.message ?? 'No se pudieron cargar las métricas')
    } finally {
      setLoading(false)
    }
  }

  async function exportCsv() {
    setCsvBusy(true)
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, full_name, role, subscription_status, subscription_plan, subscription_expires_at, session_credits_remaining, created_at, last_reminder_sent_at')
        .eq('role', 'caja_user')
        .order('created_at', { ascending: false })
      if (error) throw error
      const rows = data ?? []
      if (rows.length === 0) { alert('No hay clientes Caja para exportar.'); return }
      const headers = Object.keys(rows[0])
      const csv = [
        headers.join(','),
        ...rows.map(r => headers.map(h => csvCell(r[h])).join(',')),
      ].join('\n')
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `caja-clientes-${new Date().toISOString().slice(0,10)}.csv`
      a.click()
      URL.revokeObjectURL(a.href)
    } catch (err) {
      alert(err.message ?? 'No se pudo exportar CSV')
    } finally {
      setCsvBusy(false)
    }
  }

  const conversionPct = useMemo(() => {
    if (!metrics) return 0
    const denom = (metrics.total_caja_users ?? 0)
    if (!denom) return 0
    return Math.round(100 * (metrics.active_subs ?? 0) / denom)
  }, [metrics])

  const funnelData = useMemo(() => {
    const byPhase = new Map((funnel ?? []).map(f => [Number(f.reached_phase), Number(f.projects)]))
    return Array.from({ length: 8 }, (_, i) => ({
      phase: PHASE_NAMES[i],
      reached: byPhase.get(i - 1) ?? 0,   // -1 = sin empezar (idx 0)
    }))
  }, [funnel])

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-500 border-t-transparent" />
    </div>
  )

  if (error) return (
    <div className="card p-5 bg-red-50 border-red-200">
      <div className="flex items-start gap-2 text-red-700">
        <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <p className="font-semibold mb-1">No pudimos cargar las métricas.</p>
          <p className="text-red-600">{error}</p>
          <p className="text-xs text-red-500 mt-2">
            ¿Aplicaste <code>sql/migration_reminders_and_analytics.sql</code>? Esta vista lee de las vistas <code>caja_metrics</code>, <code>caja_signup_cohorts</code> y <code>caja_phase_funnel</code>.
          </p>
        </div>
      </div>
    </div>
  )

  const m = metrics ?? {}

  return (
    <div className="space-y-6">

      {/* ── KPI cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <KpiCard icon={DollarSign} label="MRR (USD)"    value={formatUSD(Number(m.mrr_usd ?? 0))} sublabel={`≈ ${formatCOP(Number(m.mrr_usd ?? 0) * 4200)}`} accent="emerald" />
        <KpiCard icon={TrendingUp} label="ARR (USD)"    value={formatUSD(Number(m.arr_usd ?? 0))} sublabel={`≈ ${formatCOP(Number(m.arr_usd ?? 0) * 4200)}`} accent="brand" />
        <KpiCard icon={Users}      label="Suscripciones activas" value={`${m.active_subs ?? 0}`} sublabel={`${m.active_monthly ?? 0} mensuales · ${m.active_annual ?? 0} anuales`} accent="indigo" />
        <KpiCard icon={Target}     label="Conversión trial → pago" value={`${conversionPct}%`} sublabel={`${m.active_subs ?? 0}/${m.total_caja_users ?? 0} clientes Caja`} accent="amber" />
      </div>

      {/* ── Trial pool + churned ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <KpiCard label="Total clientes Caja"  value={`${m.total_caja_users ?? 0}`} accent="gray" small />
        <KpiCard label="En trial"             value={`${m.trial_users ?? 0}`}      accent="amber" small />
        <KpiCard label="Expiradas"            value={`${m.expired_users ?? 0}`}    accent="red"   small />
        <KpiCard label="Canceladas"           value={`${m.cancelled_users ?? 0}`}  accent="gray"  small />
      </div>

      {/* ── Phase funnel ── */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Funnel de avance</h3>
            <p className="text-xs text-gray-500">Cuántos proyectos Caja han llegado a cada etapa.</p>
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={funnelData} layout="vertical" margin={{ left: 10, right: 20, top: 8, bottom: 8 }}>
              <XAxis type="number" stroke="#9ca3af" fontSize={11} />
              <YAxis dataKey="phase" type="category" width={170} stroke="#6b7280" fontSize={11} />
              <Tooltip cursor={{ fill: '#f3f4f6' }} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="reached" radius={[0, 4, 4, 0]}>
                {funnelData.map((_, i) => (
                  <Cell key={i} fill={['#94a3b8', '#475569', '#2563eb', '#7c3aed', '#0891b2', '#059669', '#ea580c', '#1a3f6b'][i] ?? '#1a3f6b'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── Signup cohorts ── */}
      <div className="card p-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Cohortes de registro</h3>
            <p className="text-xs text-gray-500">Conversión y churn por mes en que el usuario se registró.</p>
          </div>
        </div>
        {cohorts.length === 0 ? (
          <p className="text-center text-sm text-gray-400 py-8">Aún no hay cohortes para mostrar.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-5 py-2.5 font-semibold text-gray-600">Mes</th>
                <th className="text-right px-5 py-2.5 font-semibold text-gray-600">Registros</th>
                <th className="text-right px-5 py-2.5 font-semibold text-gray-600">Pagaron</th>
                <th className="text-right px-5 py-2.5 font-semibold text-gray-600">Churn</th>
                <th className="text-right px-5 py-2.5 font-semibold text-gray-600">Conv %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {cohorts.map(c => (
                <tr key={c.cohort_month} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-2.5 font-medium text-gray-700">
                    {new Date(c.cohort_month).toLocaleDateString('es', { month: 'long', year: 'numeric' })}
                  </td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-gray-700">{c.signups}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-emerald-600">{c.converted_to_paid}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums text-red-500">{c.churned}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums font-semibold text-brand-700">{c.conversion_pct ?? 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── CSV export ── */}
      <div className="card p-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-gray-900">Exportar clientes Caja</h3>
          <p className="text-xs text-gray-500 max-w-md">
            Descarga un CSV con email, plan, estado de suscripción, créditos de sesiones y fechas. Útil para outbound, retargeting, o conciliaciones.
          </p>
        </div>
        <button
          onClick={exportCsv}
          disabled={csvBusy}
          className="flex items-center gap-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors whitespace-nowrap"
        >
          {csvBusy
            ? <><Loader size={14} className="animate-spin" /> Generando…</>
            : <><Download size={14} /> Descargar CSV</>}
        </button>
      </div>
    </div>
  )
}

// ── KPI card ──────────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, sublabel, accent = 'brand', small = false }) {
  const colors = {
    brand:    { bg: 'bg-brand-50',    text: 'text-brand-600' },
    emerald:  { bg: 'bg-emerald-50',  text: 'text-emerald-600' },
    indigo:   { bg: 'bg-indigo-50',   text: 'text-indigo-600' },
    amber:    { bg: 'bg-amber-50',    text: 'text-amber-600' },
    red:      { bg: 'bg-red-50',      text: 'text-red-600' },
    gray:     { bg: 'bg-gray-50',     text: 'text-gray-600' },
  }[accent] ?? { bg: 'bg-brand-50', text: 'text-brand-600' }

  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 md:gap-3">
        {Icon && (
          <div className={`p-2 rounded-xl ${colors.bg} flex-shrink-0`}>
            <Icon size={18} className={colors.text} />
          </div>
        )}
        <div className="min-w-0">
          <div className={`${small ? 'text-lg' : 'text-xl md:text-2xl'} font-bold text-gray-900 truncate`}>{value}</div>
          <div className="text-xs text-gray-500">{label}</div>
          {sublabel && <div className="text-[10px] text-gray-400 mt-0.5 truncate">{sublabel}</div>}
        </div>
      </div>
    </div>
  )
}

function csvCell(v) {
  if (v == null) return ''
  const s = String(v).replace(/"/g, '""')
  return /[",\n]/.test(s) ? `"${s}"` : s
}
